import React, { useEffect, useState } from 'react';
import { ConfigurationSection, SpaceBetweenRow } from "@features/settings/pages/StyleComponents";
import { BodyLarge, BodyRegular } from "../../atoms/fonts/Body";
import Colours from "../../atoms/Colours";
import { Switch } from "../../atoms/Switch";
import { Checkbox } from "../../atoms/Checkbox";
import { useDispatch } from "react-redux";
import { failureNotification } from '@shared/store/notification.duck';
import { SimpleSpinner } from "../../atoms/SimpleSpinner";
import { useTranslation } from "react-i18next";
import { ReportRecipientType, ReportSchedule, ReportType, defaultReportSchedule, scheduleFromConfig } from '@shared/models/report.models';
import { getReportConfigs, updateReportConfig } from '@features/settings/services/ReportService';
import { SendSampleReportButton } from "./SendSampleReportButton";
import { ReportScheduleSetting } from "./ReportScheduleSetting";

export function VisitorSummarySetting() {
  const [isLoading, setIsLoading] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [recipientTypes, setRecipientTypes] = useState<ReportRecipientType[]>([ReportRecipientType.COMPANY_ADMINS]);
  const [schedule, setSchedule] = useState<ReportSchedule>(defaultReportSchedule(ReportType.VISITOR_SUMMARY));
  const dispatch = useDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    let active = true;
    getReportConfigs()
      .then(configs => {
        if (!active) {
          return;
        }
        const config = configs.find(c => c.reportType === ReportType.VISITOR_SUMMARY);
        if (config) {
          setEnabled(config.enabled);
          if (config.recipientTypes && config.recipientTypes.length > 0) {
            setRecipientTypes(config.recipientTypes);
          }
          setSchedule(scheduleFromConfig(config, ReportType.VISITOR_SUMMARY));
        }
      })
      .catch(() => { /* leave defaults in place */ });
    return () => { active = false; };
  }, []);

  const persist = async (next: { enabled: boolean; recipientTypes: ReportRecipientType[]; schedule: ReportSchedule }) => {
    try {
      setIsLoading(true);
      await updateReportConfig({
        reportType: ReportType.VISITOR_SUMMARY,
        enabled: next.enabled,
        recipientTypes: next.recipientTypes,
        ...next.schedule,
      });
      setEnabled(next.enabled);
      setRecipientTypes(next.recipientTypes);
      setSchedule(next.schedule);
    } catch (e: any) {
      dispatch(failureNotification('Failed to update setting, please wait a minute.'));
    } finally {
      setIsLoading(false);
    }
  };

  const onToggle = (value: boolean) => persist({ enabled: value, recipientTypes, schedule });

  const toggleRecipient = (type: ReportRecipientType, checked: boolean) => {
    const next = checked
      ? Array.from(new Set([...recipientTypes, type]))
      : recipientTypes.filter(r => r !== type);
    persist({ enabled, recipientTypes: next, schedule });
  };

  const onScheduleChange = (value: ReportSchedule) => persist({ enabled, recipientTypes, schedule: value });

  return (
    <ConfigurationSection>
      <SpaceBetweenRow>
        <BodyLarge colour={Colours.blue}>{t('settings.visitor-summary.title')}</BodyLarge>
        {isLoading ? <SimpleSpinner size={32} /> : <Switch value={enabled} label="" onChange={onToggle} />}
      </SpaceBetweenRow>
      <BodyRegular style={{ marginBottom: 16 }}>{t('settings.visitor-summary.subtitle')}</BodyRegular>
      <SendSampleReportButton reportType={ReportType.VISITOR_SUMMARY} />

      {enabled && (
        <>
          <BodyRegular weight={600} style={{ marginBottom: 8 }}>{t('settings.visitor-summary.recipients.label')}</BodyRegular>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Checkbox text={'settings.visitor-summary.recipients.company-admins'}
                      value={recipientTypes.includes(ReportRecipientType.COMPANY_ADMINS)}
                      disabled={isLoading}
                      onChange={(checked: boolean) => toggleRecipient(ReportRecipientType.COMPANY_ADMINS, checked)} />
            <Checkbox text={'settings.visitor-summary.recipients.office-admins'}
                      value={recipientTypes.includes(ReportRecipientType.OFFICE_ADMINS)}
                      disabled={isLoading}
                      onChange={(checked: boolean) => toggleRecipient(ReportRecipientType.OFFICE_ADMINS, checked)} />
          </div>
          <ReportScheduleSetting schedule={schedule}
                                 disabled={isLoading}
                                 noteKey={'settings.visitor-summary.schedule-note'}
                                 onChange={onScheduleChange} />
        </>
      )}
    </ConfigurationSection>
  );
}
